import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Loading } from "@carbon/react";
import Form from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import AutocompleteField from "../carbon/Form/AutocompleteField";
import ConfirmDialog from "../carbon/ConfirmDialog/ConfirmDialog";
import SiteDeliveryConfigFields from "./SiteDeliveryConfigFields";
import {
  editSiteValidationSchema,
  EditSiteFormValues,
} from "./editSiteValidationSchema";
import { isDuplicateInternalCodeError } from "./siteErrors";
import { SiteResponse } from "../../types";
import { useUpdateSite } from "../../hooks/useSites";
import {
  deliveryConfigToPayload,
  siteToDeliveryConfigDefaults,
} from "../../hooks/useSiteDeliveryConfig";
import { useNotification } from "../../context";
import "./editSiteForm.scss";

/**
 * The edit-site form (`PUT /site/{id}`), rebuilt on the Carbon form primitives (Task 6, Sites
 * flow) in place of 163 lines of Tailwind and hand-rolled RHF `register` wiring.
 *
 * Three defects the pre-Carbon version carried, all closed here rather than carried forward —
 * see this task's brief for the full writeup:
 *
 * 1. **`comment` was silently erased on every save.** The old `preloadData` seeded
 *    `react-hook-form` with only `internalCode`/`name` — `comment` started as `undefined`, and
 *    `onSubmit`'s `{...site, ...data}` let `data.comment` (always `""`, since the field was never
 *    pre-filled) win over the site's real, saved comment. The rewritten backend `updateSite` is
 *    full-replacement by design (see that method's own docstring) — it persists exactly what it
 *    is sent, on every field, which makes an unseeded `comment` a live data-loss bug rather than
 *    a harmless gap. `defaultValues` below seeds every editable field from `site`, `comment`
 *    included, and `handleConfirm` always sends the current value of every one of them — never a
 *    subset, and never spreading the stale `site` object over fresh form data.
 * 2. **Submitting required re-selecting the address through the Places widget, even when it was
 *    already correct.** The old `onSubmit` refused to submit at all unless a NEW `address` had
 *    been chosen in this render (`if (!address && !address?.formatted_address...)`). `address`
 *    is now a real `react-hook-form` value, seeded from `site.address`/`site.googlePlaceId` in
 *    `defaultValues` — an untouched address simply flows through unchanged; only a user who
 *    actually edits it drives it through `AutocompleteField`'s widget.
 * 3. **A duplicate `internalCode` failed silently (or hung).** `PUT /site/{id}`'s constraint
 *    handler returns `message: "Internal Code already in use."` on a conflict — the mutation's
 *    `onError` now recognises that shape (`isDuplicateInternalCodeError`, shared with
 *    `NewSiteForm.tsx`) and surfaces it as a field error on Internal Code, closing the confirm
 *    dialog rather than leaving its confirm button disabled indefinitely.
 *
 * Final fix wave (branch review) added two more:
 *
 * 4. **A null-country site threw inside `handleConfirm`.** `site.country` reads non-optional on
 *    `SiteResponse`, but nothing backend-side guarantees one exists — see the guard's own comment
 *    on `country` below. Unguarded, this dereference threw with the confirm dialog already open
 *    and its button already showing a pending spinner: the same spin-forever class the
 *    `EditSite.tsx` addendum closed for a failed fetch, just reachable from a successful one.
 * 5. **This form silently rewrote names `NewSiteForm` had stored as typed.** `capitalize()` ran
 *    here but not on create, so an untouched name got title-cased the first time anyone edited
 *    anything else on the site. Removed — this form sends what the user typed, matching
 *    `NewSiteForm` and the `comment` fix's own principle (point 1 above): presentation is not
 *    this form's job.
 */
const EditSiteForm = ({ site }: { site: SiteResponse }) => {
  const { openNotification } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [pendingValues, setPendingValues] = useState<EditSiteFormValues | null>(
    null
  );

  const defaultValues: EditSiteFormValues = useMemo(
    () => ({
      internalCode: site?.internalCode ?? "",
      name: site?.name ?? "",
      address: {
        address: site?.address ?? "",
        googlePlaceId: site?.googlePlaceId ?? "",
      },
      comment: site?.comment ?? "",
      // OC delivery config (Task 4.3) — seeded from the record like every other field, so the
      // full-replacement save below never wipes it.
      ...siteToDeliveryConfigDefaults(site),
    }),
    [site]
  );

  const { control, handleSubmit, reset, setError } =
    useForm<EditSiteFormValues>({
      resolver: yupResolver(editSiteValidationSchema),
      defaultValues,
    });

  // `site` arrives from EditSite.tsx's own fetch, after the record has already loaded (it gates
  // rendering behind `isLoading` first) — but `useForm`'s `defaultValues` only ever seeds the
  // form on the very first render, so this re-syncs if `site` shows up (or changes) later. Same
  // defensive precedent as EditUserForm.tsx.
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { mutate: updateSite, isPending: isUpdatingSite } = useUpdateSite(
    () => {
      openNotification(t("sites.editSuccess"));
      setPendingValues(null);
      navigate("/sites");
    },
    (error) => {
      setPendingValues(null);
      if (isDuplicateInternalCodeError(error)) {
        setError("internalCode", { message: t("sites.codeExists") });
      } else {
        openNotification(t("newMaterialForm.error"));
      }
    }
  );

  const handleConfirm = () => {
    if (!pendingValues || !site) return;
    updateSite({
      siteId: site.id,
      body: {
        internalCode: pendingValues.internalCode,
        // F4 (final fix wave): this used to send `capitalize(pendingValues.name)` while
        // `NewSiteForm` sent the raw typed value — so "acme depot" was stored as typed on
        // create, then silently rewritten to "Acme Depot" by any later edit that never touched
        // the name at all. Sends what the user typed now, matching `NewSiteForm` and the same
        // principle the `comment` fix already established (this form sends the truth, not a
        // presentation transform — that's CSS's job if anyone wants title-cased display).
        name: pendingValues.name,
        address: pendingValues.address.address,
        googlePlaceId: pendingValues.address.googlePlaceId,
        comment: pendingValues.comment ?? "",
        // OC delivery config (Task 4.3): rides this same save, always sent (see
        // `deliveryConfigToPayload`).
        ...deliveryConfigToPayload(pendingValues),
        // Never editable on this form (the pre-Carbon version had no country control either) —
        // resent unchanged so `updateSite`'s full-replacement write doesn't null it out.
        //
        // F3 (final fix wave): `site.country` is typed non-optional on `SiteResponse`, but
        // nothing on the backend actually guarantees a site has one — `POST /site` only
        // validates a submitted country id when the field is present at all
        // (`SiteController#createSite`), and `POST /site/admin` doesn't validate it either. A
        // null-country site used to make this line throw reading `.id` off `null` — dialog
        // already open, confirm button already showing its pending spinner, nothing left to
        // click. Guarded here instead of upstream: this form sends the truth for every field
        // (see point 1 above, the same principle the `comment` fix already established), and the
        // truth for a site with no country is `country: null`, which `updateSite`'s
        // full-replacement write then honestly persists — not a silent `{}` that would leave the
        // backend guessing.
        country: site.country?.id ? { id: site.country.id } : null,
      },
    });
  };

  if (!site)
    return <Loading withOverlay={false} description={t("cta.loading")} small />;

  return (
    <div>
      <Form
        handleSubmit={handleSubmit}
        onSubmit={(data) => setPendingValues(data)}
        noValidate
        className="edit-site-form cds-form-surface"
      >
        <div className="edit-site-form__grid">
          <TextField
            name="internalCode"
            label={t("sites.code")}
            control={control}
            required
          />
          <TextField
            name="name"
            label={t("sites.name")}
            control={control}
            required
          />
          <AutocompleteField
            name="address"
            label={t("sites.address")}
            control={control}
            required
          />
          <TextField
            name="comment"
            label={t("sites.comment")}
            control={control}
            optional
            enableCounter
            maxCount={255}
          />
        </div>

        <SiteDeliveryConfigFields control={control} />

        <div className="edit-site-form__actions">
          <Button as={Link} to="/sites" kind="secondary">
            {t("cta.cancel")}
          </Button>
          <Button type="submit">{t("cta.saveChanges")}</Button>
        </div>
      </Form>

      <ConfirmDialog
        open={pendingValues !== null}
        title={t("sites.edit")}
        body={t("sites.question")}
        confirmLabel={t("cta.edit")}
        onConfirm={handleConfirm}
        onCancel={() => setPendingValues(null)}
        isPending={isUpdatingSite}
      />
    </div>
  );
};

export default EditSiteForm;
